import * as React from 'react'
import {Link as RouterLink, type LinkProps} from 'react-router'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {type IconProp} from '@fortawesome/fontawesome-svg-core'
import {asHTMLAttributeValue} from '@freckle/maybe'
import {exhaustive} from '@freckle/exhaustive'
import {type PolyfilledEvent} from '@frontrow/console/src/polyfills'
import {cn} from '@frontrow/console/src/lib/utils'

type LinkStyleT = 'normal' | 'btn-secondary' | 'btn-danger' | 'btn-warning'

type Props = {
  onClick?: ((e: PolyfilledEvent) => void) | null
  children?: React.ReactNode
  addClass?: string
  style?: LinkStyleT
  target?: string
  rel?: string
  dataTest?: string | null
  title?: string
  leftIcon?: IconProp
} & (
  | {reactRouter: true; href: LinkProps['to']; state?: LinkProps['state']}
  | {reactRouter?: false; href: string}
)

export function Link(props: Props): React.ReactElement {
  const {
    onClick,
    children,
    dataTest,
    addClass,
    style = 'normal',
    target,
    rel,
    title,
    leftIcon
  } = props

  const styleClass = getStyleClass(style)

  const className = cn(
    'inline-block text-[inherit] cursor-pointer hover:no-underline hover:text-primary/90 focus:no-underline focus:text-primary/90',
    styleClass,
    addClass
  )

  const relValue = rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)

  const additionalProps = {
    'data-test': asHTMLAttributeValue(dataTest),
    target: asHTMLAttributeValue(target),
    rel: asHTMLAttributeValue(relValue),
    onClick: asHTMLAttributeValue(onClick),
    title: asHTMLAttributeValue(title)
  }

  const leftIconNode =
    leftIcon !== null && leftIcon !== undefined ? (
      <span className="pr-3">
        <FontAwesomeIcon icon={leftIcon} aria-hidden="true" />
      </span>
    ) : null

  const linkContentInternal = (
    <>
      {leftIconNode}
      {children}
    </>
  )

  const linkContent = isButton(style) ? (
    <span className="flex justify-center items-center h-full">{linkContentInternal}</span>
  ) : (
    linkContentInternal
  )

  return props.reactRouter === true ? (
    <RouterLink to={props.href} state={props.state} className={className} {...additionalProps}>
      {linkContent}
    </RouterLink>
  ) : (
    <a href={props.href} className={className} {...additionalProps}>
      {linkContent}
    </a>
  )
}

function getStyleClass(style: LinkStyleT): string {
  switch (style) {
    case 'normal':
      return 'text-primary'
    case 'btn-secondary':
      return 'inline-flex items-center justify-center rounded border px-4 font-medium bg-white text-primary border-primary hover:bg-white hover:text-primary-dark hover:border-primary-dark'
    case 'btn-danger':
      return 'inline-flex items-center justify-center rounded border px-4 font-medium bg-destructive text-white border-destructive hover:bg-destructive-dark hover:text-white hover:border-destructive-dark'
    case 'btn-warning':
      return 'inline-flex items-center justify-center rounded border px-4 font-medium bg-warning-brand text-white border-warning-brand hover:bg-warning-brand-dark hover:text-white hover:border-warning-brand-dark'
    default:
      return exhaustive(style)
  }
}

function isButton(linkStyle?: LinkStyleT): boolean {
  switch (linkStyle) {
    case 'btn-secondary':
    case 'btn-danger':
    case 'btn-warning':
      return true
    case 'normal':
    case undefined:
      return false
    default:
      return exhaustive(linkStyle, 'LinkStyleT')
  }
}
